import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, Loader2, Check, CalendarClock, Eye } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ReportSubjectPicker, EMPTY_MACHINE_INFO, hasMachineInfo } from "@/components/report/ReportSubjectPicker";
import { DatePicker } from "@/components/DatePicker";
import { PhotoUploader } from "@/components/PhotoUploader";
import { GpsConsent } from "@/components/geo/GpsConsent";
import { VoiceInput } from "@/components/VoiceInput";
import { AiTextAssist } from "@/components/AiTextAssist";
import { ReportSuccessDialog } from "@/components/ReportSuccessDialog";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { VoiceReportCard } from "@/components/voice/VoiceReportCard";
import { VoiceReportDialog, takeVoiceHandoff } from "@/components/voice/VoiceReportDialog";
import { hasPendingUploads, toImageRefs } from "@/lib/files";
import { useAuth, useFeatures } from "@/context/AuthContext";
import { todayISO, num } from "@/lib/format";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const CHECKLIST = [
  "Bytta motorolje og filter.",
  "Bytta luftfilter (indre og ytre).",
  "Bytta dieselfilter.",
  "Bytta hydraulikkfilter.",
  "Bytta kupéfilter.",
  "Kontrollert/bytta olje på aksling og nav.",
  "Kontrollert/bytta olje på transmisjon.",
  "Kontrollert kjølevæske og fylt på.",
  "Batteritest på startbatteri.",
  "Fylt sentralsmøring / smurt smørepunkt.",
  "Visuell kontroll av heile maskina og vask.",
];

const SERVICE_TYPES = ["250T service", "500T service", "1000T service", "2000T service", "Årsservice", "Årskontroll"];

const Step = ({ n, title, desc, children }) => (
  <Card className="shadow-sm">
    <CardHeader className="pb-3">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0B0D10] font-heading text-sm font-bold text-white dark:bg-white dark:text-[#0B0D10]">{n}</span>
        <div>
          <CardTitle className="font-heading text-base font-bold">{title}</CardTitle>
          {desc && <CardDescription className="mt-0.5">{desc}</CardDescription>}
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

export default function NyServicerapport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { map_enabled } = useFeatures();
  const [params] = useSearchParams();
  const preMachine = params.get("machine");
  const preCustomer = params.get("customer");
  const preKontroll = params.get("kontroll") === "1"; // frå kalenderen: årskontroll førehandsvald
  const preVoice = params.get("voice"); // "1" = opne opptak straks · "handoff" = analyse frå jobbrapport-skjemaet

  const [machines, setMachines] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [mode, setMode] = useState(preCustomer && !preMachine ? "customer" : "machine"); // machine | customer
  const [machineId, setMachineId] = useState(preMachine || "");
  const [customerId, setCustomerId] = useState(preCustomer || "");
  const [machineInfo, setMachineInfo] = useState(EMPTY_MACHINE_INFO);
  const [orderNumber, setOrderNumber] = useState("");
  const [date, setDate] = useState(todayISO());
  const [serviceType, setServiceType] = useState(preKontroll ? "Årskontroll" : "");
  const [hours, setHours] = useState("");
  const [checked, setChecked] = useState({});
  const [extraWork, setExtraWork] = useState("");
  const [defects, setDefects] = useState("");
  const [nextHours, setNextHours] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [controlDone, setControlDone] = useState(preKontroll);
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceNote, setVoiceNote] = useState(null); // {file_id, duration_s, transcript, summary}
  const [gps, setGps] = useState(null); // {lat,lng,accuracy_m} – berre med samtykke
  const [voiceHandoff, setVoiceHandoff] = useState(null);

  useEffect(() => {
    api.get("/machines").then((r) => setMachines(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  // Deep links: ?voice=1 opens the recorder, ?voice=handoff continues an analysis from the other form
  useEffect(() => {
    if (preVoice === "handoff") {
      const h = takeVoiceHandoff();
      if (h) { setVoiceHandoff(h); setVoiceOpen(true); }
    } else if (preVoice === "1") {
      setVoiceOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Apply the AI analysis of a voice note to the form (only the sections the user kept). */
  const applyVoice = (res, inc, note) => {
    if (inc.machine && res.machine?.matched_machine_id) { setMode("machine"); setMachineId(res.machine.matched_machine_id); }
    if (inc.service_type && res.service_type) setServiceType(res.service_type);
    if (inc.hours && res.machine_hours != null) setHours(String(res.machine_hours));
    if (inc.checklist && res.checklist_done?.length) {
      setChecked((c) => {
        const next = { ...c };
        res.checklist_done.forEach((i) => { if (CHECKLIST[i]) next[CHECKLIST[i]] = true; });
        return next;
      });
    }
    const lines = [];
    if (inc.work) lines.push(...(res.work_performed || []));
    if (inc.parts) lines.push(...(res.parts_used || []).map((p) => `Brukt: ${p.quantity != null ? `${num(p.quantity)} ${p.unit || ""} ` : ""}${p.name}`.replace(/\s+/g, " ").trim()));
    if (lines.length) setExtraWork((prev) => [prev.trim(), ...lines].filter(Boolean).join("\n"));
    if (inc.defects && res.defects) setDefects((prev) => [prev.trim(), res.defects].filter(Boolean).join("\n"));
    if (inc.next) {
      if (res.next_service_hours != null) setNextHours(String(res.next_service_hours));
      if (res.next_service_date) setNextDate(res.next_service_date);
    }
    if (inc.control && res.control_done) setControlDone(true);
    if (note?.file_id) setVoiceNote(note);
  };

  const selectedMachine = useMemo(() => (mode === "machine" ? machines.find((m) => m.id === machineId) : undefined), [mode, machines, machineId]);
  const selectedCustomer = useMemo(() => customers.find((c) => c.id === (mode === "machine" ? selectedMachine?.customer_id : customerId)), [customers, mode, selectedMachine, customerId]);
  const withMachine = mode === "machine" && !!selectedMachine;

  useEffect(() => {
    if (selectedMachine) setHours(selectedMachine.current_hours != null ? String(selectedMachine.current_hours) : "");
  }, [selectedMachine]);

  const workCount = CHECKLIST.filter((c) => checked[c]).length + extraWork.split("\n").filter((l) => l.trim()).length;

  const applyAi = (res) => {
    if (res.work_performed?.length) {
      setExtraWork((prev) => [prev.trim(), ...res.work_performed].filter(Boolean).join("\n"));
    } else if (res.description) {
      setExtraWork((prev) => [prev.trim(), res.description].filter(Boolean).join("\n"));
    }
    if (res.defects) setDefects((prev) => [prev.trim(), res.defects].filter(Boolean).join("\n"));
  };

  const [previewPayload, setPreviewPayload] = useState(null);
  const uploading = hasPendingUploads(images);

  const workItems = () => [
    ...CHECKLIST.filter((c) => checked[c]),
    ...extraWork.split("\n").map((l) => l.trim()).filter(Boolean),
  ];

  const validate = () => {
    if (mode === "machine" && !machineId) { toast.error("Vel ei maskin, eller byt til «Berre kunde»."); return false; }
    if (mode === "customer" && !customerId) { toast.error("Vel ein kunde først."); return false; }
    if (workItems().length === 0) { toast.error("Legg inn minst eitt punkt under utført arbeid."); return false; }
    if (uploading) { toast.info("Vent til bileta er ferdig lasta opp."); return false; }
    return true;
  };

  const buildPayload = () => ({
    report_type: "service",
    machine_id: mode === "machine" ? machineId : null,
    customer_id: mode === "customer" ? customerId : null,
    machine_info: mode === "customer" && hasMachineInfo(machineInfo) ? machineInfo : null,
    order_number: orderNumber.trim() || null,
    date,
    technician: user?.name,
    technician_title: user?.title,
    service_type: serviceType,
    machine_hours: hours === "" ? null : Number(hours),
    work_performed: workItems(),
    defects,
    next_service_hours: nextHours === "" ? null : Number(nextHours),
    next_service_date: nextDate || null,
    control_date: withMachine && controlDone ? date : null,
    update_machine: true,
    images: toImageRefs(images),
    voice_note: voiceNote?.file_id ? voiceNote : null,
    gps: map_enabled && withMachine ? gps || null : null,
  });

  const preview = () => { if (validate()) setPreviewPayload(buildPayload()); };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await api.post("/reports", buildPayload());
      setSaved(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikkje lagre rapport.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="service-report-form">
      <button type="button" onClick={() => navigate(-1)} className="hidden h-9 items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground lg:inline-flex" data-testid="report-back-button">
        <ArrowLeft className="h-4 w-4" /> Tilbake
      </button>
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">Ny servicerapport</h1>
        <p className="mt-1 text-sm text-muted-foreground">Fyll ut stegvis – eller snakk inn heile rapporten. PDF-en er klar med ein gong.</p>
      </div>

      <VoiceReportCard note={voiceNote} onStart={() => setVoiceOpen(true)} onRemove={() => setVoiceNote(null)} testid="service-voice-card" />

      {/* 1 */}
      <Step n="1" title="Maskin og dato" desc="Kva maskin er det utført service på? Registrert maskin – eller berre kunde for engangsjobbar.">
        <ReportSubjectPicker
          machines={machines}
          customers={customers}
          mode={mode}
          onModeChange={setMode}
          machineId={machineId}
          onMachineChange={setMachineId}
          customerId={customerId}
          onCustomerChange={setCustomerId}
          machineInfo={machineInfo}
          onMachineInfoChange={setMachineInfo}
          onMachinesChanged={(m) => setMachines((list) => (list.some((x) => x.id === m.id) ? list.map((x) => (x.id === m.id ? { ...x, ...m } : x)) : [m, ...list]))}
          onCustomersChanged={(c) => setCustomers((list) => (list.some((x) => x.id === c.id) ? list.map((x) => (x.id === c.id ? { ...x, ...c } : x)) : [c, ...list]))}
          testid="service-report-subject"
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Dato</Label>
            <DatePicker value={date} onChange={setDate} clearable={false} testid="service-report-date-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Timetal ved service (t)</Label>
            <Input className="h-11 tabular-nums" type="number" inputMode="decimal" data-testid="service-report-hours-input" value={hours} onChange={(e) => setHours(e.target.value)} placeholder={selectedMachine?.current_hours != null ? `Sist: ${num(selectedMachine.current_hours)}` : ""} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Ordrenr <span className="font-normal text-muted-foreground">– for faktura</span></Label>
            <Input className="h-11" data-testid="service-report-order-number" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="t.d. 10245" autoComplete="off" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-semibold">Servicetype</Label>
          <div className="no-scrollbar -mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
            {SERVICE_TYPES.map((t) => (
              <button key={t} type="button" onClick={() => setServiceType(t)} data-testid={`service-type-chip-${t.replace(/\s+/g, "-")}`}
                className={cn("h-9 shrink-0 rounded-full border px-3 text-xs font-semibold transition-colors", serviceType === t ? "border-transparent bg-[#0B0D10] text-white dark:bg-white dark:text-[#0B0D10]" : "bg-card hover:bg-accent")}>
                {t}
              </button>
            ))}
          </div>
          <Input className="h-11" data-testid="service-report-type-input" value={serviceType} onChange={(e) => setServiceType(e.target.value)} placeholder="Eller skriv eigen servicetype …" />
        </div>
      </Step>

      {/* 2 */}
      <Step n="2" title="Utført arbeid" desc="Trykk på punkta som er gjort, og legg til eigne punkt.">
        <div className="grid gap-2 sm:grid-cols-2" data-testid="work-checklist">
          {CHECKLIST.map((item, idx) => {
            const on = !!checked[item];
            return (
              <button key={item} type="button" onClick={() => setChecked((c) => ({ ...c, [item]: !on }))} data-testid={`work-check-${idx}`} aria-pressed={on}
                className={cn("pressable flex min-h-[48px] items-center gap-3 rounded-lg border p-3 text-left text-sm transition-colors", on ? "border-[#0B0D10] bg-[#0B0D10] text-white dark:border-white dark:bg-white dark:text-[#0B0D10]" : "bg-card hover:bg-accent")}>
                <span className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded border", on ? "border-primary bg-primary text-[#0B0D10]" : "border-border")}>{on && <Check className="h-3.5 w-3.5" />}</span>
                {item}
              </button>
            );
          })}
        </div>
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label className="text-sm font-semibold">Anna arbeid <span className="font-normal text-muted-foreground">– ei linje per punkt</span></Label>
            <div className="flex items-center gap-2">
              <VoiceInput testid="service-extra-voice" onText={(t) => setExtraWork((prev) => (prev ? `${prev}\n${t}` : t))} />
              <AiTextAssist text={extraWork} context="service" machineLabel={selectedMachine?.label} onApply={applyAi} testid="service-extra-ai" />
            </div>
          </div>
          <Textarea data-testid="service-report-extra-work" rows={4} value={extraWork} onChange={(e) => setExtraWork(e.target.value)} placeholder="Skriv eller snakk inn ekstra punkt …" className="text-base" />
        </div>
        <p className="text-xs text-muted-foreground" data-testid="work-count">{workCount} punkt registrert</p>
      </Step>

      {/* 3 */}
      <Step n="3" title="Avvik og anbefaling" desc="Kva fann du, og når bør neste service vere?">
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label className="text-sm font-semibold">Avvik / defektar funne</Label>
            <VoiceInput testid="service-defects-voice" onText={(t) => setDefects((prev) => (prev ? `${prev}\n${t}` : t))} />
          </div>
          <Textarea data-testid="service-report-defects" rows={3} value={defects} onChange={(e) => setDefects(e.target.value)} placeholder="Beskriv eventuelle feil/avvik og tiltak …" className="text-base" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Neste service (timar)</Label>
            <Input className="h-11 tabular-nums" type="number" inputMode="numeric" data-testid="service-report-next-hours" value={nextHours} onChange={(e) => setNextHours(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Neste service (dato) <span className="font-normal text-muted-foreground">{withMachine ? "– styrer fristen" : "– tilråding i PDF"}</span></Label>
            <DatePicker value={nextDate} onChange={setNextDate} testid="service-report-next-date" placeholder={selectedMachine?.service_interval_months ? `Auto: +${selectedMachine.service_interval_months} md` : "Vel dato"} />
          </div>
        </div>
        {withMachine ? (
          <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border p-3">
            <span className="flex items-center gap-3 text-sm">
              <CalendarClock className="h-5 w-5 text-muted-foreground" />
              <span><span className="block font-semibold">Årskontroll utført samtidig</span><span className="block text-xs text-muted-foreground">Set ny årskontroll-frist eitt år fram. Årskontroll kan òg registrerast separat på maskinsida.</span></span>
            </span>
            <Switch checked={controlDone} onCheckedChange={setControlDone} data-testid="service-report-control-done" />
          </label>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="service-report-no-machine-hint">Utan registrert maskin blir ingen fristar eller timetal oppdaterte – rapporten og PDF-en blir lagra på kunden.</p>
        )}
      </Step>

      {/* 4 */}
      <Step n="4" title="Bilete" desc="Ta bilete direkte eller vel frå galleri. Blir med i PDF-en.">
        <PhotoUploader value={images} onChange={setImages} testidPrefix="service-report-photo" />
        {map_enabled && withMachine && <GpsConsent value={gps} onChange={setGps} testid="service-report-gps" />}
      </Step>

      {/* Sticky action bar */}
      <div className="sticky bottom-0 z-[120] -mx-4 border-t bg-background/95 px-4 pb-[calc(var(--safe-bottom)+12px)] pt-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:-mx-0 sm:rounded-[var(--radius)] sm:border sm:px-4 sm:pb-3" data-testid="report-sticky-bar">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 text-xs text-muted-foreground">
            <div className="truncate font-semibold text-foreground">
              {mode === "machine" ? (selectedMachine?.label || "Ingen maskin vald") : (selectedCustomer ? `${selectedCustomer.name}${hasMachineInfo(machineInfo) ? ` · ${[machineInfo.brand, machineInfo.type].filter(Boolean).join(" ")}` : ""}` : "Ingen kunde vald")}
            </div>
            <div>{workCount} punkt · {images.length} bilete{orderNumber.trim() ? ` · Ordre ${orderNumber.trim()}` : ""}{uploading ? " · lastar opp …" : ""}</div>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button variant="ghost" onClick={() => navigate(-1)} className="hidden sm:inline-flex" data-testid="report-cancel-button">Avbryt</Button>
            <Button size="lg" variant="outline" onClick={preview} disabled={busy || uploading} data-testid="preview-service-report-button" aria-label="Førehandsvis PDF">
              <Eye /> <span className="hidden sm:inline">Førehandsvis</span>
            </Button>
            <Button size="lg" onClick={submit} disabled={busy || uploading} data-testid="generate-pdf-button">
              {busy || uploading ? <Loader2 className="animate-spin" /> : <FileText />} {uploading ? "Lastar opp …" : "Lagre rapport"}
            </Button>
          </div>
        </div>
      </div>

      <VoiceReportDialog
        open={voiceOpen}
        onOpenChange={(o) => { setVoiceOpen(o); if (!o) setVoiceHandoff(null); }}
        reportType="service"
        machines={machines}
        machineId={mode === "machine" ? machineId : ""}
        checklist={CHECKLIST}
        onApply={applyVoice}
        handoff={voiceHandoff}
      />
      <PdfPreviewDialog open={!!previewPayload} onOpenChange={(o) => !o && setPreviewPayload(null)} payload={previewPayload} title="Førehandsvising – servicerapport" />
      <ReportSuccessDialog report={saved} onClose={() => { setSaved(null); navigate("/rapportar"); }} newPath="/rapportar/ny-service" />
    </div>
  );
}
